import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import type { NewsItem, NewsSource } from '../types';

function timeAgo(date?: string | null): string {
  if (!date) return '—';
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function relevanceColor(score: number): string {
  if (score >= 70) return 'bg-success-wash text-success';
  if (score >= 40) return 'bg-warning-wash text-warning';
  return 'bg-gray-100 text-gray-600';
}

const CATEGORY_COLORS: Record<string, string> = {
  industrial: 'bg-info-wash text-info',
  local_market: 'bg-gray-100 text-gray-800',
  leasing: 'bg-info-wash text-info',
  investment_sales: 'bg-success-wash text-success',
  development: 'bg-warning-wash text-warning',
  regulatory: 'bg-red-100 text-red-800',
  sec_filing: 'bg-warning-wash text-warning',
  general: 'bg-gray-100 text-gray-600',
  cre_news: 'bg-info-wash text-info',
};

const SOURCE_TYPE_COLORS: Record<string, string> = {
  rss: 'bg-info-wash text-info',
  sec_edgar: 'bg-warning-wash text-warning',
  google_alert: 'bg-success-wash text-success',
  manual: 'bg-gray-100 text-gray-800',
};

export default function NewsIntelligence() {
  const [tab, setTab] = useState<'feed' | 'sources'>('feed');

  // Feed state
  const [news, setNews] = useState<NewsItem[]>([]);
  const [newsTotal, setNewsTotal] = useState(0);
  const [newsPage, setNewsPage] = useState(0);
  const [newsLoading, setNewsLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [fetchResult, setFetchResult] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [feedFilters, setFeedFilters] = useState({
    category: '', min_score: '', search: '', bookmarked_only: false,
  });

  // Sources state
  const [sources, setSources] = useState<NewsSource[]>([]);
  const [addForm, setAddForm] = useState({
    name: '', source_type: 'rss' as const, url: '', category: '', fetch_interval_hours: 6,
  });
  const [addingSource, setAddingSource] = useState(false);

  const loadNews = useCallback(async () => {
    setNewsLoading(true);
    try {
      const params: Record<string, string> = { limit: '30', offset: String(newsPage * 30) };
      if (feedFilters.category) params.category = feedFilters.category;
      if (feedFilters.min_score) params.min_score = feedFilters.min_score;
      if (feedFilters.search) params.search = feedFilters.search;
      if (feedFilters.bookmarked_only) params.bookmarked_only = 'true';
      const res = await api.listNewsItems(params);
      setNews(res.news);
      setNewsTotal(res.total);
    } catch (err) {
      console.error('Failed to load news:', err);
    } finally {
      setNewsLoading(false);
    }
  }, [newsPage, feedFilters]);

  useEffect(() => { if (tab === 'feed') loadNews(); }, [tab, loadNews]);

  useEffect(() => {
    if (tab === 'sources') {
      api.listNewsSources().then((r) => setSources(r.sources || [])).catch(console.error);
    }
  }, [tab]);

  const handleFetchAll = async () => {
    setFetching(true);
    setFetchResult('');
    try {
      const res = await api.fetchAllNewsSources();
      setFetchResult(`Fetched ${res.sources_fetched} sources, ${res.total_new} new items`);
      loadNews();
    } catch (err: any) {
      setFetchResult(`Error: ${err.message}`);
    } finally {
      setFetching(false);
    }
  };

  const handleBookmark = async (id: string) => {
    try {
      const res = await api.toggleNewsBookmark(id);
      setNews((prev) => prev.map((n) => n.id === id ? { ...n, is_bookmarked: res.is_bookmarked } : n));
    } catch (err) {
      console.error('Bookmark failed:', err);
    }
  };

  const handleDismiss = async (id: string) => {
    try {
      await api.dismissNewsItem(id);
      setNews((prev) => prev.filter((n) => n.id !== id));
      setNewsTotal((t) => t - 1);
    } catch (err) {
      console.error('Dismiss failed:', err);
    }
  };

  const handlePushToIntel = async (id: string) => {
    try {
      await api.pushNewsToMarketIntel(id);
      setNews((prev) => prev.map((n) => n.id === id ? { ...n, pushed_to_market_intel: true } : n));
      setActionMsg('Pushed to Market Intel ✓');
    } catch (err: any) {
      setActionMsg(`Push failed: ${err?.message || 'error'}`);
    } finally {
      setTimeout(() => setActionMsg(''), 4000);
    }
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleAddSource = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.name) return;
    setAddingSource(true);
    try {
      await api.addNewsSource(addForm);
      const res = await api.listNewsSources();
      setSources(res.sources || []);
      setAddForm({ name: '', source_type: 'rss', url: '', category: '', fetch_interval_hours: 6 });
    } catch (err) {
      console.error('Add source failed:', err);
    } finally {
      setAddingSource(false);
    }
  };

  const handleFetchSource = async (id: string) => {
    try {
      await api.fetchNewsSource(id);
      if (tab === 'feed') loadNews();
      const res = await api.listNewsSources();
      setSources(res.sources || []);
    } catch (err) {
      console.error('Fetch source failed:', err);
    }
  };

  const handleToggleSource = async (id: string, currentActive: boolean) => {
    try {
      await api.updateNewsSource(id, { is_active: !currentActive });
      setSources((prev) => prev.map((s) => s.id === id ? { ...s, is_active: !currentActive } : s));
    } catch (err) {
      console.error('Toggle failed:', err);
    }
  };

  const handleDeleteSource = async (id: string) => {
    try {
      await api.deleteNewsSource(id);
      setSources((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">News Intelligence</h1>
            <p className="text-sm text-gray-500 mt-1">Market news, SEC filings, and industry feeds relevant to IE industrial CRE</p>
          </div>
        </div>

        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            {[
              { key: 'feed' as const, label: 'News Feed' },
              { key: 'sources' as const, label: 'Sources' },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.key ? 'border-primary-500 text-primary-500' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        {/* News Feed Tab */}
        {tab === 'feed' && (
          <div className="space-y-4">
            {/* Fetch + Filters */}
            <div className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-3">
              <button
                onClick={handleFetchAll}
                disabled={fetching}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
              >
                {fetching ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
                Fetch All Sources
              </button>
              {fetchResult && <span className="text-sm text-gray-600">{fetchResult}</span>}
              {actionMsg && <span className={`text-sm ${actionMsg.startsWith('Push failed') ? 'text-red-600' : 'text-success'}`}>{actionMsg}</span>}
              <div className="flex-1" />
              <select
                value={feedFilters.category}
                onChange={(e) => { setFeedFilters((f) => ({ ...f, category: e.target.value })); setNewsPage(0); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All Categories</option>
                {Object.keys(CATEGORY_COLORS).map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
              <select
                value={feedFilters.min_score}
                onChange={(e) => { setFeedFilters((f) => ({ ...f, min_score: e.target.value })); setNewsPage(0); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All Scores</option>
                <option value="25">25+</option>
                <option value="50">50+</option>
                <option value="75">75+</option>
              </select>
              <input
                type="text"
                placeholder="Search..."
                value={feedFilters.search}
                onChange={(e) => { setFeedFilters((f) => ({ ...f, search: e.target.value })); setNewsPage(0); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-48"
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={feedFilters.bookmarked_only}
                  onChange={(e) => { setFeedFilters((f) => ({ ...f, bookmarked_only: e.target.checked })); setNewsPage(0); }}
                  className="rounded"
                />
                Bookmarked
              </label>
            </div>

            {/* News cards */}
            {newsLoading ? (
              <div className="bg-white rounded-xl border p-12 text-center text-gray-400">Loading news...</div>
            ) : news.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
                No news items. Click "Fetch All Sources" to pull latest feeds.
              </div>
            ) : (
              <div className="space-y-3">
                {news.map((item) => (
                  <div key={item.id} className="bg-white rounded-xl border p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Category + Relevance badges */}
                        <div className="flex items-center gap-2 mb-1">
                          {item.relevance_category && (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[item.relevance_category] || CATEGORY_COLORS.general}`}>
                              {item.relevance_category.replace(/_/g, ' ')}
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${relevanceColor(item.relevance_score)}`}>
                            {item.relevance_score}
                          </span>
                          {item.pushed_to_market_intel && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">In Intel</span>
                          )}
                        </div>
                        {/* Title */}
                        {item.url ? (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-semibold text-gray-900 hover:text-primary-600"
                          >
                            {item.title}
                          </a>
                        ) : (
                          <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                        )}
                        {/* Summary */}
                        {item.summary && (
                          <p className="text-sm text-gray-500 mt-1">
                            {expanded.has(item.id) ? item.summary : item.summary.slice(0, 200)}
                            {item.summary.length > 200 && (
                              <button onClick={() => toggleExpand(item.id)} className="text-primary-500 ml-1 text-xs">
                                {expanded.has(item.id) ? 'Show less' : '...more'}
                              </button>
                            )}
                          </p>
                        )}
                        {/* Meta */}
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                          <span>{(item as any).news_sources?.name || 'Unknown source'}</span>
                          <span>{timeAgo(item.published_at)}</span>
                        </div>
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => handleBookmark(item.id)}
                        className={`p-1.5 rounded-lg transition-colors ${item.is_bookmarked ? 'text-warning bg-warning-wash' : 'text-gray-400 hover:text-warning hover:bg-warning-wash'}`}
                        title={item.is_bookmarked ? 'Unbookmark' : 'Bookmark'}
                      >
                        <svg className="w-4 h-4" fill={item.is_bookmarked ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDismiss(item.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="Dismiss"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      {!item.pushed_to_market_intel && (
                        <button
                          onClick={() => handlePushToIntel(item.id)}
                          className="ml-auto px-3 py-1.5 text-xs font-medium text-primary-500 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                        >
                          Push to Market Intel
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {newsTotal > 30 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Showing {newsPage * 30 + 1}–{Math.min((newsPage + 1) * 30, newsTotal)} of {newsTotal}</span>
                <div className="flex gap-2">
                  <button onClick={() => setNewsPage((p) => Math.max(0, p - 1))} disabled={newsPage === 0} className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50">Previous</button>
                  <button onClick={() => setNewsPage((p) => p + 1)} disabled={(newsPage + 1) * 30 >= newsTotal} className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50">Next</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sources Tab */}
        {tab === 'sources' && (
          <div className="space-y-6">
            {/* Sources table */}
            <div className="bg-white rounded-xl border overflow-hidden">
              {sources.length === 0 ? (
                <div className="p-12 text-center text-gray-400">No news sources configured</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">URL</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Active</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Fetched</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Interval</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sources.map((s) => (
                        <tr key={s.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-900 font-medium">{s.name}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SOURCE_TYPE_COLORS[s.source_type] || 'bg-gray-100 text-gray-800'}`}>
                              {s.source_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs max-w-[200px] truncate">{s.url || '—'}</td>
                          <td className="px-4 py-3 text-gray-600">{s.category || '—'}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleToggleSource(s.id, s.is_active)}
                              className={`w-10 h-5 rounded-full relative transition-colors ${s.is_active ? 'bg-success' : 'bg-gray-300'}`}
                            >
                              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${s.is_active ? 'left-5' : 'left-0.5'}`} />
                            </button>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{timeAgo(s.last_fetched_at)}</td>
                          <td className="px-4 py-3 text-center text-gray-600">{s.fetch_interval_hours}h</td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button onClick={() => handleFetchSource(s.id)} className="text-xs text-primary-500 hover:text-primary-600 font-medium">
                                Fetch
                              </button>
                              <button onClick={() => handleDeleteSource(s.id)} className="text-xs text-red-500 hover:text-red-700 font-medium">
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Add source form */}
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Add New Source</h3>
              <form onSubmit={handleAddSource} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                    <input
                      type="text"
                      value={addForm.name}
                      onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                    <select
                      value={addForm.source_type}
                      onChange={(e) => setAddForm((f) => ({ ...f, source_type: e.target.value as any }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="rss">RSS</option>
                      <option value="sec_edgar">SEC EDGAR</option>
                      <option value="google_alert">Google Alert</option>
                      <option value="manual">Manual</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">URL</label>
                    <input
                      type="text"
                      value={addForm.url}
                      onChange={(e) => setAddForm((f) => ({ ...f, url: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                    <input
                      type="text"
                      value={addForm.category}
                      onChange={(e) => setAddForm((f) => ({ ...f, category: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="e.g. cre_news"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Interval (hrs)</label>
                    <input
                      type="number"
                      value={addForm.fetch_interval_hours}
                      onChange={(e) => setAddForm((f) => ({ ...f, fetch_interval_hours: parseInt(e.target.value) || 6 }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      min={1}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={addingSource || !addForm.name}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
                >
                  {addingSource ? 'Adding...' : 'Add Source'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
